import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from '../../../redux/modalSlice';
import { useRequest } from '../../../hooks/UseRequest';
import { setNotification } from '../../../redux/notificationSlice';
import { triggerRerender } from '../../../redux/rerenderSlice';

export default function RedeemForPaydownModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const requests = useRequest();
  const inFlightCustomerId = useRef(null);
  const active = modal.is_active && modal.type === 'redeem_for_paydown_modal';
  const customerId = modal.options?.customerId;

  const [available, setAvailable] = useState(modal.options?.available ?? 0);
  const [pointsPerDollar, setPointsPerDollar] = useState(modal.options?.pointsPerDollar ?? 0);
  const [loanId, setLoanId] = useState(modal.options?.loanId);
  const refreshed = useRef(false);

  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [balanceReady, setBalanceReady] = useState(false);

  const numericAmount = Number(amount) || 0;
  const remaining = available - numericAmount;

  // The balance the dashlet passed in via modal.options can be stale by the time the agent
  // opens this modal — refresh it from the source of truth before validating a submission
  // against it.
  useEffect(() => {
    const refreshBalance = async () => {
      requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/reward_summary/dashlet`);
      try {
        const response = await requests.get();
        // Ignore a response that a close or a newer request has superseded.
        if (inFlightCustomerId.current !== customerId) return;
        setAvailable(response.data.data.available ?? 0);
        setPointsPerDollar(response.data.data.points_per_dollar ?? 0);
        setLoanId(response.data.data.loan_id);
      } catch (err) {
        // Best-effort refresh — keep the snapshot passed in via modal.options on failure.
      } finally {
        if (inFlightCustomerId.current === customerId) {
          inFlightCustomerId.current = null;
          setBalanceReady(true);
        }
      }
    };
    if (active && customerId && inFlightCustomerId.current !== customerId && !refreshed.current) {
      inFlightCustomerId.current = customerId;
      refreshed.current = true;
      // Show the opener's snapshot, not the mount-time 0 or the last customer's figure, until the refresh lands.
      setAvailable(modal.options?.available ?? 0);
      setPointsPerDollar(modal.options?.pointsPerDollar ?? 0);
      setLoanId(modal.options?.loanId);
      refreshBalance();
    }
  }, [active, customerId]);

  // This modal stays mounted in the Footer, so every close path (not only Cancel) must leave the
  // next opening to start clean — a leftover amount or refresh flag would carry to the next customer.
  useEffect(() => {
    if (!active) resetState();
  }, [active]);

  // Points buy whole dollars only, so a non-multiple would be deducted in full while applying
  // less to the loan. Mirrors CustomerRewardRuleService, which is the server-side authority.
  const validateAmount = (value) => {
    if (value === '' || value === null) return '';
    const numeric = Number(value);
    if (!Number.isInteger(numeric) || numeric <= 0) return 'Enter a whole number of points greater than 0.';
    if (numeric > available) return 'Amount cannot exceed the Available balance.';
    if (!pointsPerDollar) return '';
    if (numeric < pointsPerDollar) return `Points must be redeemed in groups of ${pointsPerDollar}.`;
    if (numeric % pointsPerDollar !== 0) {
      const nearest = Math.floor(numeric / pointsPerDollar) * pointsPerDollar;
      return `Redeem in increments of ${pointsPerDollar} points. Try ${nearest}.`;
    }
    return '';
  };

  // Derived on every render, so a balance refresh that lands after the agent typed re-validates the amount.
  const amountError = validateAmount(amount) || submitError;

  const resetState = () => {
    setAmount('');
    setSubmitError('');
    setSaving(false);
    setPointsPerDollar(modal.options?.pointsPerDollar ?? 0);
    refreshed.current = false;
    setBalanceReady(false);
    // Abandon any in-flight request so its response can't land on the next customer.
    inFlightCustomerId.current = null;
  };

  const handleCancel = () => {
    resetState();
    dispatch(setModal({ is_active: false }));
  };

  const handleConfirm = async () => {
    const numericAmount = Number(amount);
    const validationError = amount === '' ? 'Enter an amount greater than 0.' : validateAmount(amount);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }
    setSubmitError('');
    setSaving(true);
    requests.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/reward_paydown`);
    try {
      const response = await requests.post({ amount: numericAmount });
      if (response.status === 200 || response.status === 201) {
        const remainingAfterRedeem = available - numericAmount;
        setSaving(false);
        dispatch(setNotification({
          type: 'success',
          body: `${numericAmount} pts redeemed and applied to ${loanId ? `Loan #${loanId}` : 'the loan'}'s balance. ${remainingAfterRedeem} pts remain available.`
        }));
        dispatch(triggerRerender({ subpanelId: 'reward_point_dashlet' }));
        handleCancel();
      } else {
        setSaving(false);
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }));
      }
    } catch (error) {
      setSaving(false);
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme;
      dispatch(setNotification({ type: 'error', body: errorMessage }));
    }
  };

  if (!(modal.is_active && modal.type === 'redeem_for_paydown_modal')) {
    return null;
  }

  return (
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div className="modal-card column is-4">
        <header className="modal-card-head py-3">
          <p className="modal-card-title">Redeem Points</p>
          <button className="delete" aria-label="close" onClick={handleCancel}></button>
        </header>
        <section className="modal-card-body pt-4 pb-4">
          <p className="mb-4">
            Select the number of points to redeem toward the balance of {loanId ? `Loan #${loanId}` : 'this loan'}.
          </p>

          <p className="has-text-weight-bold mb-4">Available: {available} pts</p>

          <div className="field">
            <label className="label">Points to Redeem</label>
            <div className="control">
              <div style={{ position: 'relative' }}>
                <input
                  type="number"
                  className="input"
                  data-testid="redeem-for-paydown-modal-amount-input"
                  style={{ paddingRight: '3rem' }}
                  value={amount}
                  min={0}
                  max={available}
                  step={1}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setSubmitError('');
                  }}
                />
                <span
                  className="has-text-grey"
                  style={{ position: 'absolute', top: '50%', right: '0.75rem', transform: 'translateY(-50%)', pointerEvents: 'none' }}
                >
                  pts
                </span>
              </div>
            </div>
            <p
              className={amountError ? 'has-text-danger is-size-7 mt-1' : 'has-text-grey is-size-7 mt-1'}
              data-testid="redeem-for-paydown-modal-amount-error"
            >
              {amountError || `Max ${available} pts — whole points only.`}
            </p>
          </div>

          <div className="is-flex is-justify-content-space-between is-align-items-center has-background-light p-3 mt-4">
            <span className="has-text-grey">Remaining Balance if Redeemed</span>
            <span className="has-text-weight-bold">{remaining} pts</span>
          </div>
        </section>
        <footer className="modal-card-foot is-justify-content-end py-3">
          <div className="lox-form__buttons-wrapper has-text-right">
            <button type="button" className="button is-small has-background-grey has-text-white" data-testid="redeem-for-paydown-modal-cancel" onClick={handleCancel} disabled={saving}>Cancel</button>
            <button type="button" className="button is-link is-small" data-testid="redeem-for-paydown-modal-confirm" onClick={handleConfirm} disabled={saving || !balanceReady || !!amountError || amount === ''}>Redeem</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
